<template>
  <div class="pa-3">
    <!-- Los totales salen de las filas que pasan los filtros, no del archivo completo. -->
    <v-row dense class="mb-1">
      <v-col cols="6" sm="4" md="2">
        <TarjetaKpi titulo="Órdenes" :valor="fmtNum(totales.ordenes)" icono="mdi-clipboard-text-outline"
          color="indigo" :cargando="cargando" />
      </v-col>
      <v-col cols="6" sm="4" md="2">
        <TarjetaKpi titulo="Renglones" :valor="fmtNum(totales.renglones)" icono="mdi-format-list-numbered"
          color="blue-grey" :cargando="cargando" />
      </v-col>
      <v-col cols="6" sm="4" md="2">
        <TarjetaKpi titulo="Piezas a producir" :valor="fmtNum(totales.piezas)" icono="mdi-package-variant-closed"
          color="teal" :cargando="cargando" />
      </v-col>
      <v-col cols="6" sm="4" md="2">
        <TarjetaKpi titulo="Máquinas con órdenes" :valor="fmtNum(totales.maquinas)" icono="mdi-robot-industrial"
          color="deep-purple" :cargando="cargando" />
      </v-col>
      <v-col cols="6" sm="4" md="2">
        <TarjetaKpi titulo="Moldes" :valor="fmtNum(totales.moldes)" icono="mdi-cube-outline"
          color="cyan" :cargando="cargando" />
      </v-col>
      <v-col cols="6" sm="4" md="2">
        <TarjetaKpi titulo="Artículos" :valor="fmtNum(totales.articulos)" icono="mdi-tag-outline"
          color="orange" :cargando="cargando" />
      </v-col>
    </v-row>

    <v-alert v-if="error" type="error" dense outlined class="mb-2">{{ error }}</v-alert>
    <v-alert v-else-if="mensaje" type="info" dense outlined class="mb-2">{{ mensaje }}</v-alert>

    <!-- ===================== FILTROS ===================== -->
    <!-- Cada dimensión tiene su modo: "Solo" deja únicamente lo seleccionado; "Todos menos" quita
         lo seleccionado y deja pasar el resto. Sin selección el filtro no aplica. -->
    <v-card v-if="filtrosVisibles.length" outlined class="filtros mb-2">
      <div v-for="f in filtrosVisibles" :key="f.clave" class="filtro">
        <div class="filtro-cabecera">
          <span class="filtro-titulo">{{ f.etiqueta }}</span>
          <v-btn-toggle v-model="f.modo" mandatory dense group class="filtro-modo">
            <v-btn x-small value="solo" text title="Mostrar únicamente lo seleccionado">Solo</v-btn>
            <v-btn x-small value="excepto" text title="Mostrar todo excepto lo seleccionado">Todos menos</v-btn>
          </v-btn-toggle>
        </div>
        <v-select v-model="f.valores" :items="opciones[f.clave]" multiple chips small-chips deletable-chips
          clearable dense outlined hide-details :placeholder="`Todas (${opciones[f.clave].length})`"
          :menu-props="{ maxHeight: 320 }" :color="f.modo === 'excepto' ? 'deep-orange' : 'primary'">
          <template v-slot:selection="{ item, index }">
            <v-chip v-if="index < 2" x-small :color="f.modo === 'excepto' ? 'deep-orange lighten-4' : ''" close
              @click:close="quitarValor(f, item.value)">
              <v-icon v-if="f.modo === 'excepto'" x-small left>mdi-minus-circle-outline</v-icon>{{ item.text }}
            </v-chip>
            <span v-else-if="index === 2" class="caption grey--text ml-1">+{{ f.valores.length - 2 }}</span>
          </template>
          <template v-slot:item="{ item, attrs, on }">
            <v-list-item v-bind="attrs" dense v-on="on">
              <v-list-item-action class="mr-2">
                <v-checkbox :input-value="attrs.inputValue" dense hide-details />
              </v-list-item-action>
              <v-list-item-content>
                <v-list-item-title>{{ item.text }}</v-list-item-title>
              </v-list-item-content>
              <span class="caption grey--text">{{ fmtNum(item.conteo) }}</span>
            </v-list-item>
          </template>
        </v-select>
      </div>

      <div class="filtro filtro--acciones">
        <v-btn small text :disabled="!hayFiltros" @click="limpiarFiltros">
          <v-icon left small>mdi-filter-remove-outline</v-icon> Limpiar filtros
        </v-btn>
      </div>
    </v-card>

    <div class="d-flex align-center flex-wrap mb-2" style="gap: 8px;">
      <v-text-field v-model="busqueda" dense outlined hide-details clearable placeholder="Buscar en cualquier columna…"
        prepend-inner-icon="mdi-magnify" style="max-width: 340px;" />
      <v-spacer />
      <span class="caption grey--text">
        <template v-if="archivo"><v-icon x-small class="mr-1">mdi-file-delimited-outline</v-icon>{{ archivo }} · </template>
        {{ fmtNum(itemsFiltrados.length) }} de {{ fmtNum(items.length) }} renglones
        <template v-if="items.length"> · todo el archivo, sin filtrar por periodo</template>
      </span>
    </div>

    <v-data-table :headers="headers" :items="itemsFiltrados" :search="busqueda" :loading="cargando" dense
      :items-per-page="50" :footer-props="{ 'items-per-page-options': [25, 50, 100, 500, -1] }"
      :height="altoTabla(itemsFiltrados.length)" :fixed-header="!!altoTabla(itemsFiltrados.length)"
      class="tabla" loading-text="Cargando... Espere, Por Favor." :no-data-text="textoVacio" />
  </div>
</template>

<script>
import axios from 'axios'
import TarjetaKpi from './TarjetaKpi.vue'
import { prop, fmtNum, altoTabla, mensajeError } from './utils'

// Nombre de columna sin acentos, espacios ni mayúsculas, para casar encabezados del CSV
// aunque QAD los mande con variaciones ("Articulo a producir", "ARTICULOAPRODUCIR", ...).
const clave = (s) => String(s || '')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .replace(/[^a-z0-9]/gi, '')
  .toLowerCase()

const VACIO = '(vacío)'

// Columnas que se muestran alineadas a la derecha por ser numéricas en el CSV de QAD.
const NUMERICAS = ['cantidadaproducir', 'posicionarticulo', 'rateprogramado']

export default {
  name: 'CargaOtsArchivoCsv',

  components: { TarjetaKpi },

  data() {
    return {
      cargando: false,
      error: '',
      mensaje: '',
      archivo: '',
      columnas: [],
      // Un objeto por fila del CSV con llaves c0..cN (la posición de la columna): así los
      // nombres del encabezado, que pueden traer puntos o espacios, no rompen la tabla.
      items: [],
      busqueda: '',
      // Dimensiones filtrables. "columnas" son los encabezados aceptados, en orden de
      // preferencia; si el archivo no trae ninguno, ese filtro no se muestra.
      filtros: [
        { clave: 'maquina', etiqueta: 'Máquina', columnas: ['Maquina', 'Máquina'], modo: 'solo', valores: [] },
        { clave: 'molde', etiqueta: 'Molde', columnas: ['Molde'], modo: 'solo', valores: [] },
        { clave: 'articulo', etiqueta: 'Artículo', columnas: ['ArticuloAProducir', 'Articulo'], modo: 'solo', valores: [] },
        { clave: 'estatus', etiqueta: 'Estatus', columnas: ['Estatus'], modo: 'solo', valores: [] },
        { clave: 'estado', etiqueta: 'Estado', columnas: ['Estado'], modo: 'solo', valores: [] },
      ],
    }
  },

  computed: {
    // Índice de cada columna del CSV por su nombre normalizado.
    indicePorNombre() {
      const mapa = {}
      this.columnas.forEach((c, i) => {
        const k = clave(c)
        if (!(k in mapa)) mapa[k] = i
      })
      return mapa
    },

    // Índices de las columnas que usan los totales (−1 si el archivo no la trae).
    idx() {
      const buscar = (...nombres) => {
        for (const n of nombres) {
          const i = this.indicePorNombre[clave(n)]
          if (i !== undefined) return i
        }
        return -1
      }
      return {
        claveOt: buscar('ClaveOT', 'Clave'),
        cantidad: buscar('CantidadAProducir', 'Cantidad'),
        maquina: buscar('Maquina', 'Máquina'),
        molde: buscar('Molde'),
        articulo: buscar('ArticuloAProducir', 'Articulo'),
      }
    },

    filtrosVisibles() {
      return this.filtros.filter((f) => this.indiceFiltro(f) >= 0)
    },

    hayFiltros() {
      return this.filtros.some((f) => f.valores.length > 0)
    },

    // Valores distintos de cada dimensión sobre TODO el archivo, con cuántos renglones traen
    // cada uno. Se calculan sobre el total (y no sobre lo ya filtrado) para que en "Todos
    // menos" lo excluido siga apareciendo en la lista y se pueda volver a incluir.
    opciones() {
      const resultado = {}
      this.filtros.forEach((f) => {
        const i = this.indiceFiltro(f)
        if (i < 0) {
          resultado[f.clave] = []
          return
        }
        const conteos = new Map()
        this.items.forEach((it) => {
          const v = this.valorFiltro(it, i)
          conteos.set(v, (conteos.get(v) || 0) + 1)
        })
        resultado[f.clave] = [...conteos.entries()]
          .sort((a, b) => a[0].localeCompare(b[0], 'es', { numeric: true }))
          .map(([v, n]) => ({ text: v, value: v, conteo: n }))
      })
      return resultado
    },

    itemsFiltrados() {
      const activos = this.filtros
        .map((f) => ({ f, i: this.indiceFiltro(f), set: new Set(f.valores) }))
        .filter((x) => x.i >= 0 && x.set.size > 0)
      if (!activos.length) return this.items

      return this.items.filter((it) => activos.every(({ f, i, set }) => {
        const seleccionado = set.has(this.valorFiltro(it, i))
        return f.modo === 'excepto' ? !seleccionado : seleccionado
      }))
    },

    totales() {
      const { claveOt, cantidad, maquina, molde, articulo } = this.idx
      const ordenes = new Set()
      const maquinas = new Set()
      const moldes = new Set()
      const articulos = new Set()
      let piezas = 0

      this.itemsFiltrados.forEach((it) => {
        if (claveOt >= 0) { const v = this.texto(it, claveOt); if (v) ordenes.add(v.toUpperCase()) }
        if (maquina >= 0) { const v = this.texto(it, maquina); if (v) maquinas.add(v.toUpperCase()) }
        if (molde >= 0) { const v = this.texto(it, molde); if (v) moldes.add(v.toUpperCase()) }
        if (articulo >= 0) { const v = this.texto(it, articulo); if (v) articulos.add(v.toUpperCase()) }
        if (cantidad >= 0) {
          const n = Number(String(this.texto(it, cantidad)).replace(/,/g, ''))
          if (Number.isFinite(n)) piezas += n
        }
      })

      return {
        ordenes: ordenes.size,
        renglones: this.itemsFiltrados.length,
        piezas,
        maquinas: maquinas.size,
        moldes: moldes.size,
        articulos: articulos.size,
      }
    },

    // Encabezados con el mismo nombre que trae el CSV.
    headers() {
      return this.columnas.map((c, i) => ({
        text: c,
        value: `c${i}`,
        align: NUMERICAS.includes(clave(c)) ? 'end' : 'start',
      }))
    },

    textoVacio() {
      if (!this.items.length) return 'La última carga no tiene un CSV de órdenes que mostrar.'
      return 'Ningún renglón del archivo pasa los filtros elegidos.'
    },
  },

  methods: {
    fmtNum,
    altoTabla,

    indiceFiltro(f) {
      for (const n of f.columnas) {
        const i = this.indicePorNombre[clave(n)]
        if (i !== undefined) return i
      }
      return -1
    },

    texto(item, i) {
      const v = item[`c${i}`]
      return v == null ? '' : String(v).trim()
    },

    valorFiltro(item, i) {
      return this.texto(item, i) || VACIO
    },

    quitarValor(f, valor) {
      f.valores = f.valores.filter((v) => v !== valor)
    },

    limpiarFiltros() {
      this.filtros.forEach((f) => {
        f.valores = []
        f.modo = 'solo'
      })
    },

    // Lo llama el contenedor. El rango se ignora a propósito: esta pestaña muestra el archivo
    // completo; el periodo solo aplica a Información y Comparativa.
    async cargar() {
      this.cargando = true
      this.$emit('cargando', true)
      this.error = ''
      this.mensaje = ''
      try {
        const { data } = await axios.get('/CargaOts/Csv')
        this.archivo = prop(data, 'archivo') || ''
        this.columnas = prop(data, 'columnas') || []
        this.items = (prop(data, 'filas') || []).map((fila, n) => {
          const it = { _n: n }
          this.columnas.forEach((c, i) => { it[`c${i}`] = fila[i] })
          return it
        })
        this.mensaje = prop(data, 'mensaje') || ''
        // Selecciones que ya no existen en el archivo nuevo se descartan.
        this.filtros.forEach((f) => {
          const validos = new Set((this.opciones[f.clave] || []).map((o) => o.value))
          f.valores = f.valores.filter((v) => validos.has(v))
        })
        this.$emit('auditoria', prop(data, 'auditoria') || null)
        this.$emit('carga-en-curso', prop(data, 'cargaEnCurso') || null)
      } catch (e) {
        this.archivo = ''
        this.columnas = []
        this.items = []
        this.error = mensajeError(e, 'No se pudo leer el CSV de la última carga.')
        // eslint-disable-next-line no-console
        console.error('Error consultando el CSV original de la carga de OTs', e)
      } finally {
        this.cargando = false
        this.$emit('cargando', false)
      }
    },
  },
}
</script>

<style scoped>
.tabla >>> td,
.tabla >>> th {
  white-space: nowrap;
}

.filtros {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 8px 12px;
  padding: 8px 12px;
}

.filtro {
  flex: 1 1 200px;
  min-width: 200px;
  max-width: 320px;
}

.filtro--acciones {
  flex: 0 0 auto;
  min-width: 0;
  padding-bottom: 2px;
}

.filtro-cabecera {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 2px;
}

.filtro-titulo {
  font-size: 12px;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.6);
}

.filtro-modo >>> .v-btn {
  min-width: 0 !important;
  padding: 0 6px !important;
  text-transform: none;
  letter-spacing: 0;
}
</style>
